/**
 * Completed screening questionnaire response ids, keyed by SKU, for a Mercy booking.
 *
 * Screening finishes before the visit is booked. The book call later tells the API which
 * response to attach, so the id has to survive the pages between the questionnaire and
 * checkout. Only the SKU id and the response id are stored — never answers.
 * Other tenants do not send the id.
 */

const MERCY_TENANT_ID = 'mercy'

/** Mercy is the tenant whose booking request carries the screening response id. */
export function includesScreeningQuestionnaireResponse(tenantId: string | null | undefined): boolean {
    return tenantId === MERCY_TENANT_ID
}

const SCREENING_QUESTIONNAIRE_RESPONSE_BY_SKU_KEY = 'screeningQuestionnaireResponseBySku'

type ScreeningQuestionnaireResponseBySku = Record<string, string>

function isBrowser(): boolean {
    return typeof window !== 'undefined'
}

function presentId(value: string | null | undefined): string | undefined {
    if (typeof value !== 'string') return undefined
    const trimmed = value.trim()
    return trimmed.length > 0 ? trimmed : undefined
}

/**
 * Questionnaire routes carry the SKU id as a string. Visit responses return
 * `appointments.sku_id` as an integer, so both forms have to find the same entry.
 */
function presentSkuId(value: string | number | null | undefined): string | undefined {
    if (typeof value === 'number') {
        if (!Number.isFinite(value)) return undefined
        return String(value)
    }
    return presentId(value)
}

function readMap(): ScreeningQuestionnaireResponseBySku {
    if (!isBrowser()) return {}
    try {
        const raw = window.sessionStorage.getItem(SCREENING_QUESTIONNAIRE_RESPONSE_BY_SKU_KEY)
        if (!raw) return {}
        const parsed: unknown = JSON.parse(raw)
        if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) return {}

        const map: ScreeningQuestionnaireResponseBySku = {}
        for (const [skuId, questionnaireResponseId] of Object.entries(parsed)) {
            const storedSkuId = presentSkuId(skuId)
            const presentQuestionnaireResponseId = presentId(
                typeof questionnaireResponseId === 'string' ? questionnaireResponseId : undefined,
            )
            if (storedSkuId && presentQuestionnaireResponseId) {
                map[storedSkuId] = presentQuestionnaireResponseId
            }
        }
        return map
    } catch {
        return {}
    }
}

function writeMap(map: ScreeningQuestionnaireResponseBySku): void {
    if (!isBrowser()) return
    try {
        if (Object.keys(map).length === 0) {
            window.sessionStorage.removeItem(SCREENING_QUESTIONNAIRE_RESPONSE_BY_SKU_KEY)
            return
        }
        window.sessionStorage.setItem(SCREENING_QUESTIONNAIRE_RESPONSE_BY_SKU_KEY, JSON.stringify(map))
    } catch {
        // sessionStorage may be unavailable (private mode / quota). Booking still proceeds
        // without the id; the API books the visit and records that the response was not sent.
    }
}

/** Replace the completed screening response id for this SKU. */
export function rememberScreeningQuestionnaireResponse(
    skuId: string | number,
    questionnaireResponseId: string,
): void {
    const storedSkuId = presentSkuId(skuId)
    const presentQuestionnaireResponseId = presentId(questionnaireResponseId)
    if (!storedSkuId || !presentQuestionnaireResponseId) return

    const map = readMap()
    map[storedSkuId] = presentQuestionnaireResponseId
    writeMap(map)
}

/** Drop the stored screening response for this SKU. Other SKUs are left in place. */
export function clearScreeningQuestionnaireResponse(skuId: string | number): void {
    const storedSkuId = presentSkuId(skuId)
    if (!storedSkuId) return

    const map = readMap()
    if (!(storedSkuId in map)) return
    delete map[storedSkuId]
    writeMap(map)
}

export function readScreeningQuestionnaireResponseId(
    skuId: string | number | null | undefined,
): string | undefined {
    const storedSkuId = presentSkuId(skuId)
    if (!storedSkuId) return undefined
    return readMap()[storedSkuId]
}

/**
 * Book-mutation fields for the SKU being booked. Empty outside Mercy, and empty when
 * this SKU has no stored screening response, so the optional request field is left off.
 */
export function screeningQuestionnaireResponseBookFields(
    skuId: string | number | null | undefined,
    tenantId: string | null | undefined,
): {
    screeningQuestionnaireResponseId?: string
} {
    if (!includesScreeningQuestionnaireResponse(tenantId)) return {}
    const screeningQuestionnaireResponseId = readScreeningQuestionnaireResponseId(skuId)
    return screeningQuestionnaireResponseId ? { screeningQuestionnaireResponseId } : {}
}
